import { useState, useEffect, useRef } from "react";
import { Phone, Mail, ChevronDown, CheckCircle2, Shield, FileCheck, Zap, Users, Clock, Award, ArrowRight, AlertTriangle, Building2, Briefcase, Calendar, ClipboardCheck, Settings, UserCheck, MessageCircle, FileText, Wrench, Target, MapPin, ListChecks, Send } from "lucide-react";
import useSeo from "./useSeo";

const B2BPage = () => {
  useSeo({
    title: "Für Solarfirmen | PV-Abnahme & Projektabschluss – ENTHIDA",
    description: "Zuverlässiger externer Partner für Solarfirmen: PV-Abnahme, Inbetriebnahme, Dokumentation und Kundenübergabe. Flexibel, eigenverantwortlich, vollständig versichert.",
    path: "/fuer-solarfirmen",
  });
  const [openFaq, setOpenFaq] = useState(null);
  const [isHeroContactOpen, setIsHeroContactOpen] = useState(false);
  const heroContactRef = useRef(null);

  // Projekt-Check Form State
  const [form, setForm] = useState({
    projektart: "",
    ort: "",
    anzahl: "",
    zeitraum: "",
    leistungen: [],
    nachricht: "",
  });

  const toggleLeistung = (val) => {
    setForm((prev) => ({
      ...prev,
      leistungen: prev.leistungen.includes(val)
        ? prev.leistungen.filter((l) => l !== val)
        : [...prev.leistungen, val],
    }));
  };

  const buildMessage = () => {
    const lines = [
      "Neuer Projekt-Check über enthida.de",
      "",
      `Projektart: ${form.projektart || "—"}`,
      `Ort / Region: ${form.ort || "—"}`,
      `Anzahl Anlagen: ${form.anzahl || "—"}`,
      `Zeitraum: ${form.zeitraum || "—"}`,
      `Benötigt: ${form.leistungen.length ? form.leistungen.join(", ") : "—"}`,
    ];
    if (form.nachricht.trim()) {
      lines.push("", "Nachricht:", form.nachricht.trim());
    }
    return lines.join("\n");
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/4917641234614?text=${encodeURIComponent(buildMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleEmail = () => {
    const subject = "Projekt-Check Anfrage (Solarfirma)";
    const url = `mailto:info@enthida.de?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildMessage())}`;
    window.location.href = url;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (heroContactRef.current && !heroContactRef.current.contains(event.target)) {
        setIsHeroContactOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const zeitverluste = [
    { icon: Clock, title: "Fehlende Abnahmen", desc: "Projekte stehen still, weil die finale Prüfung und Inbetriebnahme fehlt." },
    { icon: Users, title: "Personalmangel", desc: "Zu wenige Fachkräfte für die wachsende Anzahl an Projekten." },
    { icon: FileText, title: "Offene Projekte", desc: "Anlagen sind installiert, aber nicht abgeschlossen – Kunden warten." },
    { icon: ClipboardCheck, title: "Fehlende Dokumentation", desc: "Prüfprotokolle und Unterlagen fehlen oder sind unvollständig." },
    { icon: Building2, title: "Netzbetreiber-Verzögerungen", desc: "Anmeldungen und Kommunikation kosten Zeit und Nerven." },
    { icon: MessageCircle, title: "Kundenrückfragen", desc: "Endkunden verstehen ihre Anlage nicht und rufen ständig an." },
    { icon: AlertTriangle, title: "Unsaubere Übergaben", desc: "Projekte ohne Einweisung führen zu Unzufriedenheit." },
    { icon: Settings, title: "Speicherintegration", desc: "Komplexe Systeme erfordern Zeit und Spezialwissen." },
    { icon: Target, title: "Projektspitzen", desc: "Saisonale Schwankungen überlasten das Team." },
    { icon: Calendar, title: "Urlaubs- & Krankheitsausfälle", desc: "Projekte müssen verschoben werden." },
  ];

  const leistungen = [
    { icon: CheckCircle2, title: "PV-Abnahme", desc: "Normgerechte Prüfung der gesamten Anlage nach VDE" },
    { icon: Zap, title: "Inbetriebnahme", desc: "Fachgerechte Inbetriebnahme mit allen Einstellungen" },
    { icon: Settings, title: "VDE-Messungen", desc: "Alle erforderlichen Messungen und Protokolle" },
    { icon: FileCheck, title: "Dokumentation", desc: "Vollständige Prüfprotokolle und Unterlagen" },
    { icon: UserCheck, title: "Kundenübergabe", desc: "Professionelle Einweisung Ihrer Endkunden" },
    { icon: Building2, title: "Netzbetreiber-Anmeldung", desc: "Komplette Kommunikation mit dem Netzbetreiber" },
    { icon: ClipboardCheck, title: "Marktstammdatenregister", desc: "Eintrag und Registrierung im MaStR" },
    { icon: Target, title: "Smart Meter Vorbereitung", desc: "Infrastruktur für intelligente Messsysteme" },
    { icon: Shield, title: "Überspannungsschutz-Prüfung", desc: "Kontrolle und Dokumentation des Blitzschutzes" },
    { icon: Briefcase, title: "Speicherintegration", desc: "AC-seitige Einbindung von Batteriespeichern" },
    { icon: Wrench, title: "Fehlersuche", desc: "Diagnose und Behebung bei Problemprojekten" },
    { icon: Award, title: "Projektunterstützung", desc: "Flexible Hilfe bei komplexen Vorhaben" },
  ];

  const zusammenarbeit = [
    { icon: Target, title: "Projektbezogene Einsätze", desc: "Sie haben ein konkretes Projekt? Ich übernehme die Abnahme und Dokumentation." },
    { icon: Clock, title: "Kurzfristige Unterstützung", desc: "Engpass? Oft kann ich innerhalb weniger Tage einspringen." },
    { icon: Calendar, title: "Urlaubsvertretung", desc: "Ihr Elektriker ist im Urlaub? Ich halte die Projekte am Laufen." },
    { icon: Users, title: "Feste Partnerschaft", desc: "Regelmäßige Zusammenarbeit mit planbaren Kapazitäten." },
    { icon: Settings, title: "Flexible Kapazitäten", desc: "Von einzelnen Projekten bis zu mehreren pro Woche." },
    { icon: Shield, title: "Eigenverantwortlich", desc: "Ich arbeite selbstständig, versichert und dokumentiere alles sauber." },
  ];

  const ablauf = [
    { num: "01", title: "Projekt abstimmen", desc: "Kurze Abstimmung per Telefon oder WhatsApp: Was, wann, wo?" },
    { num: "02", title: "Einsatz planen", desc: "Terminierung und Vorbereitung – Sie erhalten eine Bestätigung." },
    { num: "03", title: "Vor-Ort-Abnahme", desc: "PV-Abnahme, Inbetriebnahme, Messungen, Kundeneinweisung." },
    { num: "04", title: "Dokumentation übergeben", desc: "Alle Unterlagen digital – Prüfprotokolle, Fotos, Anmeldungen." },
    { num: "05", title: "Projekt abgeschlossen", desc: "Ihr Kunde ist zufrieden, Sie haben ein Problem weniger." },
  ];

  const projekte = [
    { title: "EFH mit Speicher", desc: "10 kWp + 10 kWh Speicher, Komplettabnahme und Kundenübergabe", tag: "Standard" },
    { title: "Nachrüstung Überspannungsschutz", desc: "Bestandsanlage normgerecht absichern", tag: "Bestand" },
    { title: "Gewerbeanlage 50 kWp", desc: "Größere Anlage mit Smart Meter und Monitoring", tag: "Gewerbe" },
    { title: "Problemprojekt", desc: "Fehlersuche und Abschluss einer liegengebliebenen Anlage", tag: "Support" },
    { title: "Urlaubsvertretung", desc: "3 Wochen alle Abnahmen für Installateur übernommen", tag: "Vertretung" },
    { title: "Kundenübergabe", desc: "Nachträgliche Einweisung nach Fertigstellung durch anderen Betrieb", tag: "Service" },
  ];

  const faqs = [
    {
      question: "Wie läuft die Zusammenarbeit konkret ab?",
      answer: "Ganz unkompliziert: Sie melden sich mit einem Projekt, wir klären kurz den Umfang, ich fahre zum Termin, führe Abnahme und Inbetriebnahme durch, weise den Kunden ein und übergebe Ihnen alle Unterlagen digital. Sie müssen sich um nichts weiter kümmern."
    },
    {
      question: "Können Sie auch kurzfristig einspringen?",
      answer: "In der Regel ja. Bei Engpässen oder Ausfällen bin ich oft innerhalb weniger Tage verfügbar. Rufen Sie einfach an oder schreiben per WhatsApp – wir finden eine Lösung."
    },
    {
      question: "Wie ist das mit der Haftung geregelt?",
      answer: "Ich arbeite eigenverantwortlich und bin vollständig versichert (Betriebshaftpflicht). Die Abnahme und Dokumentation erfolgt in meinem Namen – sauber getrennt, für beide Seiten rechtssicher."
    },
    {
      question: "Was kostet die Zusammenarbeit?",
      answer: "Die Kosten richten sich nach Projektumfang und Entfernung. Für Standardprojekte (EFH mit PV, optional Speicher) gibt es Pauschalpreise. Gerne erstelle ich Ihnen ein individuelles Angebot."
    },
    {
      question: "Machen Sie auch die Netzbetreiber-Anmeldung?",
      answer: "Ja, auf Wunsch übernehme ich die komplette Kommunikation mit dem Netzbetreiber sowie den Eintrag im Marktstammdatenregister. Sie erhalten alle Bestätigungen."
    },
    {
      question: "Können Sie auch Speicher in Betrieb nehmen?",
      answer: "Ja, die AC-seitige Speicherintegration gehört zu meinen Kernleistungen. Ich parametriere das System, binde es ins Energiemanagement ein und erkläre dem Kunden die Funktionsweise."
    },
    {
      question: "Wie schnell erhalte ich die Dokumentation?",
      answer: "In der Regel innerhalb von 24-48 Stunden nach dem Einsatz. Sie bekommen alle Unterlagen digital: Prüfprotokolle, Fotos, Anlagenübersicht, Kundenunterschriften."
    },
    {
      question: "Arbeiten Sie auch mit mehreren Solarfirmen zusammen?",
      answer: "Ja, ich unterstütze verschiedene Installateure und Solarunternehmen in der Region. Diskretion ist selbstverständlich – jede Zusammenarbeit ist vertraulich."
    },
    {
      question: "Was passiert, wenn bei der Prüfung Mängel auffallen?",
      answer: "Ich dokumentiere alle Mängel mit Fotos und Beschreibung. Kleinere Dinge behebe ich direkt vor Ort. Bei größeren Mängeln informiere ich Sie sofort, damit Sie reagieren können. Die Abnahme erfolgt erst, wenn alles sicher ist."
    },
    {
      question: "Können Sie auch Kundeneinweisungen nachholen?",
      answer: "Ja, das kommt häufiger vor. Wenn ein Projekt von einem anderen Betrieb installiert wurde und der Kunde nicht eingewiesen ist, übernehme ich die professionelle Übergabe und Erklärung der Anlage."
    },
    {
      question: "In welchem Umkreis sind Sie tätig?",
      answer: "Ich bin im Umkreis von 100 km um Eggenfelden tätig. Das umfasst große Teile von Niederbayern und Oberbayern. Für größere Projekte oder regelmäßige Zusammenarbeit bin ich nach Absprache auch weiter entfernt verfügbar."
    },
    {
      question: "Bieten Sie auch eine feste Partnerschaft an?",
      answer: "Ja, bei regelmäßiger Zusammenarbeit können wir feste Kapazitäten vereinbaren. Das gibt Ihnen Planungssicherheit und mir ermöglicht es, meine Termine optimal zu koordinieren."
    },
    {
      question: "Wie erreiche ich Sie am besten?",
      answer: "Am schnellsten per WhatsApp oder Telefon unter +49 176 41234 614. Für Projektanfragen nutzen Sie gerne auch E-Mail: info@enthida.de. Ich melde mich in der Regel am selben Tag."
    },
    {
      question: "Können Sie auch bei Problemanlagen helfen?",
      answer: "Ja, Fehlersuche und Projektrettung gehören zu meinem Angebot. Ob fehlerhafte Einstellungen, Kommunikationsprobleme oder unvollständige Installationen – ich analysiere das Problem und finde eine Lösung."
    },
    {
      question: "Wie läuft die Abrechnung?",
      answer: "Nach Projektabschluss erhalten Sie eine ordentliche Rechnung. Bei regelmäßiger Zusammenarbeit sind auch Sammelrechnungen möglich. Zahlungsziel ist 14 Tage netto."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero */}
      <section className="relative pt-32 pb-32">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20 overflow-hidden"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/d607ac6b-4513-4b85-be87-20e8a661ad1d/images/b709392042161b55856c9b63d53c7e53b4c60478295f6b6f124bbd8c04b73dfc.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Für Solarfirmen & Installateure</p>
          <h1 className="text-4xl md:text-6xl tracking-tighter font-medium text-white mb-6">
            Ihr zuverlässiger Partner für
            <br />
            <span className="bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">PV-Abnahme & Projektabschluss.</span>
          </h1>
          <p className="text-lg text-[#a1a1aa] max-w-2xl leading-relaxed mb-8">
            PV-Inbetriebnahme, VDE-Prüfung, Dokumentation und Kundenübergabe – strukturiert, zuverlässig und eigenverantwortlich. 
            Für Solarfirmen, die sich auf ihre Kernkompetenz konzentrieren wollen.
          </p>
          
          {/* Hero Contact Dropdown */}
          {/* Hero Contact + Quick-Check */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
            <div className="relative inline-block" ref={heroContactRef}>
              <button 
                onClick={() => setIsHeroContactOpen(!isHeroContactOpen)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.02] shadow-[0_0_20px_rgba(0,212,170,0.3)]"
                data-testid="b2b-hero-cta"
              >
                <Phone size={20} />
                Jetzt Kontakt aufnehmen
                <ChevronDown size={18} className={`transition-transform duration-300 ${isHeroContactOpen ? "rotate-180" : ""}`} />
            </button>
            
            {isHeroContactOpen && (
              <div className="absolute left-0 top-full mt-3 w-72 bg-[#121212] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-fadeIn z-50">
                <div className="p-3 space-y-1">
                  <a href="tel:+4917641234614" className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center">
                      <Phone size={16} className="text-[#00d4aa]" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">Telefon</p>
                      <p className="text-[#00d4aa] text-xs">+49 176 41234 614</p>
                    </div>
                  </a>
                  <a href="mailto:info@enthida.de" className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center">
                      <Mail size={16} className="text-[#00d4aa]" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">E-Mail</p>
                      <p className="text-[#00d4aa] text-xs">info@enthida.de</p>
                    </div>
                  </a>
                  <a href="https://wa.me/4917641234614" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center">
                      <MessageCircle size={16} className="text-[#00d4aa]" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">WhatsApp</p>
                      <p className="text-[#00d4aa] text-xs">Schnelle Antwort</p>
                    </div>
                  </a>
                </div>
              </div>
            )}
            </div>
            
            {/* Sekundärer Anker zum Projekt-Check */}
            <a
              href="#projekt-check"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm text-white text-sm font-medium hover:border-[#00d4aa]/50 hover:bg-white/10 transition-all duration-300"
              data-testid="b2b-hero-quickcheck"
            >
              <ListChecks size={16} className="text-[#00d4aa]" />
              Projekt-Check starten
              <ArrowRight size={14} className="text-[#71717a] group-hover:text-[#00d4aa] group-hover:translate-x-0.5 transition-all" />
            </a>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <Shield size={18} className="text-[#00d4aa]" />
              <span>Vollständig versichert</span>
            </div>
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <CheckCircle2 size={18} className="text-[#00d4aa]" />
              <span>VDE-zertifiziert</span>
            </div>
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <Clock size={18} className="text-[#00d4aa]" />
              <span>Kurzfristig verfügbar</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky WhatsApp */}
      <a
        href="https://wa.me/4917641234614"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-5 py-3 rounded-full shadow-[0_0_30px_rgba(0,212,170,0.4)] hover:scale-105 transition-all duration-300 flex items-center gap-2"
      >
        <MessageCircle size={20} />
        <span className="hidden sm:inline">WhatsApp</span>
      </a>

      {/* Stats */}
      <section className="py-16 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent mb-2">24h</div>
              <p className="text-[#a1a1aa] text-sm">Reaktionszeit</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent mb-2">100%</div>
              <p className="text-[#a1a1aa] text-sm">VDE-konform</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent mb-2">100km</div>
              <p className="text-[#a1a1aa] text-sm">Einsatzradius</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent mb-2">0</div>
              <p className="text-[#a1a1aa] text-sm">Papierkram für Sie</p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Message */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="bg-gradient-to-r from-[#121212] to-[#18181b] border border-white/10 rounded-2xl p-8 lg:p-12">
            <p className="text-2xl md:text-3xl text-white font-medium leading-relaxed">
              ENTHIDA steht für <span className="text-[#00d4aa]">strukturierte Zusammenarbeit, saubere Übergaben und professionelle Projektabschlüsse</span>.
            </p>
            <p className="text-[#a1a1aa] mt-4">
              Ein zuverlässiger externer Partner für Solarfirmen, die sich auf das konzentrieren wollen, was sie am besten können: Installieren.
            </p>
          </div>
        </div>
      </section>

      {/* Wo Solarfirmen Zeit verlieren */}
      <section className="py-24 bg-[#0a0a0a]" id="probleme">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Die Realität im Tagesgeschäft</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Wo Solarfirmen oft Zeit verlieren
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Diese Herausforderungen kennen viele Installateure und PV-Unternehmen. ENTHIDA unterstützt genau an diesen Punkten.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {zeitverluste.map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-orange-500/20 rounded-xl p-5 hover:border-orange-500/40 transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                    <item.icon size={20} className="text-orange-500/70" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">{item.title}</h3>
                    <p className="text-[#71717a] text-sm">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#7ed321]/10 to-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-full px-6 py-3">
              <CheckCircle2 className="text-[#00d4aa]" size={20} />
              <span className="text-white font-medium">ENTHIDA unterstützt Sie genau an diesen Punkten.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Was ENTHIDA übernimmt */}
      <section className="py-24 bg-[#0a0a0a]" id="leistungen">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Konkrete Entlastung</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Was ENTHIDA konkret übernimmt
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Von der PV-Abnahme bis zur Kundenübergabe – alle Leistungen für einen professionellen Projektabschluss.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {leistungen.map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-xl p-5 hover:border-[#00d4aa]/30 transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center mb-4 group-hover:shadow-[0_0_15px_rgba(0,212,170,0.2)] transition-all">
                  <item.icon size={20} className="text-[#00d4aa]" />
                </div>
                <h3 className="text-white font-medium mb-1">{item.title}</h3>
                <p className="text-[#71717a] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flexible Zusammenarbeit */}
      <section className="py-24 bg-[#0a0a0a]" id="zusammenarbeit">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Partnerschaft auf Augenhöhe</p>
              <h2 className="text-3xl md:text-4xl tracking-tighter font-medium text-white mb-6">
                Flexible Zusammenarbeit
              </h2>
              <p className="text-[#a1a1aa] text-lg mb-8">
                Ob einzelnes Projekt oder feste Partnerschaft – die Zusammenarbeit passt sich Ihren Anforderungen an. 
                Professionell, eigenverantwortlich und immer zuverlässig.
              </p>
              
              <div className="space-y-4">
                {zusammenarbeit.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-[#121212] rounded-xl border border-white/5 hover:border-[#00d4aa]/20 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center shrink-0">
                      <item.icon className="text-[#00d4aa]" size={20} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">{item.title}</h3>
                      <p className="text-[#71717a] text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/d607ac6b-4513-4b85-be87-20e8a661ad1d/images/8f0d2e22d9fff561149ff980148efee607e21d7b5776e31a443b82ac34179e85.png"
                  alt="Professionelle Elektroinstallation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 md:-left-8 bg-[#121212] border border-white/10 rounded-2xl p-5 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7ed321] to-[#00d4aa] flex items-center justify-center">
                    <Shield className="text-black" size={24} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Vollständig versichert</p>
                    <p className="text-[#71717a] text-sm">Eigenverantwortlich</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-24 bg-[#0a0a0a]" id="ablauf">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Strukturiert & Effizient</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              So läuft die Zusammenarbeit
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Von der Anfrage bis zum abgeschlossenen Projekt – ein klarer, effizienter Prozess.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {ablauf.map((step, index) => (
              <div key={step.num} className="relative">
                <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 h-full hover:border-[#00d4aa]/30 transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7ed321] to-[#00d4aa] flex items-center justify-center mb-4 text-black font-bold text-lg">
                    {step.num}
                  </div>
                  <h3 className="text-white font-medium mb-2">{step.title}</h3>
                  <p className="text-[#71717a] text-sm">{step.desc}</p>
                </div>
                {index < ablauf.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="text-[#00d4aa]/30" size={20} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Typische Projekte */}
      <section className="py-24 bg-[#0a0a0a]" id="projekte">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Beispiele aus der Praxis</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Typische Projekte
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Von der Standard-Abnahme bis zum Problemprojekt – ein Überblick über typische Einsätze.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projekte.map((projekt, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-2xl p-6 hover:border-[#00d4aa]/30 transition-all duration-300 hover:-translate-y-1"
              >
                <span className="inline-block px-3 py-1 bg-[#00d4aa]/10 text-[#00d4aa] text-xs font-medium rounded-full mb-4">
                  {projekt.tag}
                </span>
                <h3 className="text-xl font-medium text-white mb-2">{projekt.title}</h3>
                <p className="text-[#71717a] text-sm">{projekt.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="relative">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-8xl text-[#00d4aa]/20 font-serif">"</div>
            <blockquote className="relative z-10">
              <p className="text-2xl md:text-3xl text-white font-medium leading-relaxed mb-8">
                Endlich jemand, der uns die Abnahmen abnimmt. Schnell, zuverlässig, sauber dokumentiert. Unsere Kunden sind zufrieden, wir haben mehr Zeit fürs Kerngeschäft.
              </p>
              <footer className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7ed321]/30 to-[#00d4aa]/30 flex items-center justify-center">
                  <span className="text-white font-bold">SB</span>
                </div>
                <div className="text-left">
                  <p className="text-white font-medium">Solar-Betrieb</p>
                  <p className="text-[#71717a] text-sm">Installateur-Partner aus Niederbayern</p>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#0a0a0a]" id="faq">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Häufige Fragen</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Alles zur Zusammenarbeit
            </h2>
            <p className="text-[#a1a1aa]">
              Antworten auf die wichtigsten Fragen von Solarfirmen und Installateuren.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden hover:border-[#00d4aa]/20 transition-colors"
              >
                <button
                  className="w-full p-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className={`font-medium pr-4 transition-colors ${openFaq === index ? "text-[#00d4aa]" : "text-white"}`}>
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`text-[#00d4aa] shrink-0 transition-transform duration-300 ${openFaq === index ? "rotate-180" : ""}`}
                    size={20}
                  />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? "max-h-96" : "max-h-0"}`}>
                  <p className="px-5 pb-5 text-[#a1a1aa] leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center p-6 bg-[#121212] border border-white/10 rounded-2xl">
            <p className="text-[#a1a1aa] mb-4">Weitere Fragen? Sprechen wir darüber.</p>
            <a href="tel:+4917641234614" className="inline-flex items-center gap-2 text-[#00d4aa] font-medium hover:text-white transition-colors">
              <Phone size={18} />
              +49 176 41234 614
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Projekt-Check Form */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" id="projekt-check">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 -left-32 w-96 h-96 bg-[#7ed321]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 -right-32 w-96 h-96 bg-[#00d4aa]/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-[#00d4aa]/20 rounded-full px-4 py-2 mb-6">
              <ListChecks size={14} className="text-[#00d4aa]" />
              <span className="text-[#a1a1aa] text-xs uppercase tracking-[0.18em] font-semibold">Für Solarfirmen & Installateure</span>
            </div>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Projekt kurz <span className="bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">abstimmen</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
              In 1 Minute ausfüllen – ich melde mich persönlich zurück.
            </p>
          </div>

          <div className="relative bg-gradient-to-br from-[#121212] to-[#0e0e0e] border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl shadow-black/40">
            <div className="absolute -top-px left-12 right-12 h-px bg-gradient-to-r from-transparent via-[#00d4aa]/50 to-transparent" />

            {/* Grid 1: Projektart + Ort */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Projektart */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">1</span>
                  Projektart
                </label>
                <div className="relative">
                  <select
                    value={form.projektart}
                    onChange={(e) => setForm({ ...form, projektart: e.target.value })}
                    className="w-full appearance-none bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 pr-10 text-white text-sm focus:outline-none focus:border-[#00d4aa]/50 focus:ring-2 focus:ring-[#00d4aa]/10 transition-all cursor-pointer hover:border-white/20"
                    data-testid="form-projektart"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="EFH">EFH</option>
                    <option value="Gewerbe">Gewerbe</option>
                    <option value="Speicherprojekt">Speicherprojekt</option>
                    <option value="Nachrüstung">Nachrüstung</option>
                    <option value="Problemprojekt">Problemprojekt</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#00d4aa] pointer-events-none" />
                </div>
              </div>

              {/* Ort */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">2</span>
                  Ort / Region
                </label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                  <input
                    type="text"
                    value={form.ort}
                    onChange={(e) => setForm({ ...form, ort: e.target.value })}
                    placeholder="z.B. Eggenfelden, Landshut, Niederbayern"
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white text-sm placeholder:text-[#52525b] focus:outline-none focus:border-[#00d4aa]/50 focus:ring-2 focus:ring-[#00d4aa]/10 transition-all hover:border-white/20"
                    data-testid="form-ort"
                  />
                </div>
              </div>
            </div>

            {/* Grid 2: Anzahl + Zeitraum als Pill-Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              {/* Anzahl */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">3</span>
                  Anzahl Anlagen
                </label>
                <div className="flex flex-wrap gap-2">
                  {["1", "2–5", "5+"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm({ ...form, anzahl: opt })}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                        form.anzahl === opt
                          ? "bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black border-transparent shadow-[0_0_15px_rgba(0,212,170,0.25)]"
                          : "bg-[#0a0a0a] text-[#a1a1aa] border-white/10 hover:border-[#00d4aa]/40 hover:text-white"
                      }`}
                      data-testid={`form-anzahl-${opt.replace(/[^a-z0-9]/gi, "")}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Zeitraum */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">4</span>
                  Zeitraum
                </label>
                <div className="flex flex-wrap gap-2">
                  {["Sofort", "Diese Woche", "Diesen Monat", "Planbar"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm({ ...form, zeitraum: opt })}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                        form.zeitraum === opt
                          ? "bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black border-transparent shadow-[0_0_15px_rgba(0,212,170,0.25)]"
                          : "bg-[#0a0a0a] text-[#a1a1aa] border-white/10 hover:border-[#00d4aa]/40 hover:text-white"
                      }`}
                      data-testid={`form-zeitraum-${opt.replace(/[^a-z0-9]/gi, "")}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Leistungen Checkboxen */}
            <div className="mt-6">
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-3">
                <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">5</span>
                Was wird benötigt?
                <span className="text-[#71717a] text-xs font-normal">(Mehrfachauswahl)</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { val: "Abnahme", icon: CheckCircle2 },
                  { val: "Inbetriebnahme", icon: Zap },
                  { val: "Dokumentation", icon: FileCheck },
                  { val: "Kundeneinweisung", icon: UserCheck },
                  { val: "Netzbetreiber", icon: Building2 },
                  { val: "Fehlersuche", icon: Wrench },
                ].map(({ val, icon: Icon }) => {
                  const active = form.leistungen.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => toggleLeistung(val)}
                      className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 text-left ${
                        active
                          ? "bg-gradient-to-br from-[#7ed321]/10 to-[#00d4aa]/10 border-[#00d4aa]/50 text-white"
                          : "bg-[#0a0a0a] border-white/10 text-[#a1a1aa] hover:border-white/20 hover:text-white"
                      }`}
                      data-testid={`form-leistung-${val.toLowerCase()}`}
                    >
                      <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${active ? "bg-gradient-to-br from-[#7ed321] to-[#00d4aa] border-transparent" : "border-white/20"}`}>
                        {active && <CheckCircle2 size={12} className="text-black" strokeWidth={3} />}
                      </span>
                      <Icon size={14} className={active ? "text-[#00d4aa]" : "text-[#71717a]"} />
                      <span>{val}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Nachricht */}
            <div className="mt-6">
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">6</span>
                Nachricht <span className="text-[#71717a] text-xs font-normal">(optional)</span>
              </label>
              <textarea
                value={form.nachricht}
                onChange={(e) => setForm({ ...form, nachricht: e.target.value })}
                rows={3}
                placeholder="Kurze Info zum Projekt, Besonderheiten, Wunschtermin …"
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-[#52525b] focus:outline-none focus:border-[#00d4aa]/50 focus:ring-2 focus:ring-[#00d4aa]/10 transition-all hover:border-white/20 resize-none"
                data-testid="form-nachricht"
              />
            </div>

            {/* Submit Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleWhatsApp}
                className="group flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-6 py-4 rounded-xl transition-all duration-300 hover:scale-[1.01] shadow-[0_0_25px_rgba(0,212,170,0.3)]"
                data-testid="form-submit-whatsapp"
              >
                <MessageCircle size={18} />
                Kapazität prüfen
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                type="button"
                onClick={handleEmail}
                className="group flex-1 flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-300 hover:border-[#00d4aa]/50 hover:bg-white/10"
                data-testid="form-submit-email"
              >
                <Send size={18} className="text-[#00d4aa]" />
                Per E-Mail senden
              </button>
            </div>

            <p className="mt-4 text-[#71717a] text-xs text-center">
              Ihre Angaben werden direkt an mich übermittelt. Keine Speicherung, keine Weitergabe.
            </p>
          </div>

          {/* Trust Row */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: Clock, label: "Flexible Unterstützung" },
              { icon: Shield, label: "Eigenverantwortliches Arbeiten" },
              { icon: Award, label: "Vollständig versichert" },
              { icon: MessageCircle, label: "Ruhige Kundenkommunikation" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#121212]/60 border border-white/5 rounded-xl px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7ed321]/15 to-[#00d4aa]/15 flex items-center justify-center shrink-0">
                  <item.icon size={15} className="text-[#00d4aa]" />
                </div>
                <span className="text-[#a1a1aa] text-xs md:text-sm font-medium leading-tight">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#7ed321]/10 to-[#00d4aa]/10 rounded-full blur-3xl animate-pulse" />
        </div>
        
        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="bg-gradient-to-br from-[#121212] to-[#0a0a0a] border border-white/10 rounded-3xl p-10 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#7ed321]/20 via-transparent to-[#00d4aa]/20 opacity-50" />
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#00d4aa]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#7ed321]/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-6">
                    <span className="w-2 h-2 bg-[#00d4aa] rounded-full animate-pulse" />
                    <span className="text-[#a1a1aa] text-sm">Kapazitäten verfügbar</span>
                  </div>
                  
                  <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
                    Lassen Sie uns
                    <span className="block bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">zusammenarbeiten.</span>
                  </h2>
                  <p className="text-lg text-[#a1a1aa] mb-6">
                    Ob kurzfristige Unterstützung oder langfristige Partnerschaft – ich bin bereit.
                  </p>
                  
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-[#71717a] text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-[#00d4aa]" />
                      <span>Flexibel einsetzbar</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-[#00d4aa]" />
                      <span>Vollständig versichert</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <a 
                    href="tel:+4917641234614"
                    className="group flex items-center gap-4 p-5 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] rounded-2xl hover:scale-[1.02] transition-all duration-300 shadow-[0_0_30px_rgba(0,212,170,0.3)]"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-black/20 flex items-center justify-center shrink-0">
                      <Phone size={24} className="text-black" />
                    </div>
                    <div className="flex-1">
                      <p className="text-black font-bold text-lg">Direkt anrufen</p>
                      <p className="text-black/70">+49 176 41234 614</p>
                    </div>
                    <ArrowRight size={20} className="text-black group-hover:translate-x-1 transition-transform" />
                  </a>
                  
                  <a 
                    href="mailto:info@enthida.de"
                    className="group flex items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-2xl hover:border-[#00d4aa]/50 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                      <Mail size={24} className="text-[#00d4aa]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold text-lg">E-Mail schreiben</p>
                      <p className="text-[#a1a1aa]">info@enthida.de</p>
                    </div>
                    <ArrowRight size={20} className="text-[#71717a] group-hover:text-[#00d4aa] group-hover:translate-x-1 transition-all" />
                  </a>
                  
                  <a 
                    href="https://wa.me/4917641234614"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-2xl hover:border-[#00d4aa]/50 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                      <MessageCircle size={24} className="text-[#00d4aa]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold text-lg">WhatsApp</p>
                      <p className="text-[#a1a1aa]">Schnelle Antwort garantiert</p>
                    </div>
                    <ArrowRight size={20} className="text-[#71717a] group-hover:text-[#00d4aa] group-hover:translate-x-1 transition-all" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2BPage;
